import { formatPatientTelephoneNumbers } from '@/lib/patientStamkortFields';

export type PatientListSortColumn = 'name' | 'cpr' | 'email' | 'phone' | 'clinic';
export type PatientListSortDirection = 'asc' | 'desc';

export interface PatientListSort {
  column: PatientListSortColumn;
  direction: PatientListSortDirection;
}

/** Matches the list query: last name, then first name, A→Å. */
export const DEFAULT_PATIENT_LIST_SORT: PatientListSort = {
  column: 'name',
  direction: 'asc',
};

export interface PatientListSortable {
  id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  telephone_subscribers?: unknown;
  has_cpr: boolean;
  clinic_name?: string | null;
}

const TEXT: Intl.CollatorOptions = { sensitivity: 'base', numeric: true };

/**
 * Clicking the active column flips direction. Clicking another column
 * starts at A→Å / low→high.
 */
export function nextPatientListSort(
  current: PatientListSort,
  column: PatientListSortColumn,
): PatientListSort {
  if (current.column === column) {
    return { column, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { column, direction: 'asc' };
}

export function sortPatientList<T extends PatientListSortable>(
  rows: readonly T[],
  sort: PatientListSort,
): T[] {
  return [...rows].sort((a, b) => comparePatientListItems(a, b, sort));
}

export function comparePatientListItems(
  a: PatientListSortable,
  b: PatientListSortable,
  sort: PatientListSort,
): number {
  let cmp = 0;
  switch (sort.column) {
    case 'name':
      cmp = compareText(a.last_name, b.last_name, sort.direction);
      if (cmp === 0) cmp = compareText(a.first_name, b.first_name, sort.direction);
      break;
    case 'email':
      cmp = compareText(a.email, b.email, sort.direction);
      break;
    case 'phone':
      cmp = compareText(phoneSortKey(a), phoneSortKey(b), sort.direction);
      break;
    case 'cpr':
      // CPR is encrypted. The list only knows whether one is stored, so this
      // column groups “has CPR” together and keeps a missing CPR last.
      cmp = compareText(a.has_cpr ? '1' : '', b.has_cpr ? '1' : '', sort.direction);
      break;
    case 'clinic':
      cmp = compareText(a.clinic_name, b.clinic_name, sort.direction);
      break;
    default: {
      const unexpected: never = sort.column;
      return unexpected;
    }
  }
  if (cmp !== 0) return cmp;
  return compareNameTie(a, b);
}

function phoneSortKey(row: PatientListSortable): string {
  return formatPatientTelephoneNumbers(row.telephone_subscribers, row.phone);
}

/** Missing values stay last in both directions. Ties keep surname order. */
function compareText(
  a: string | null | undefined,
  b: string | null | undefined,
  direction: PatientListSortDirection,
): number {
  const aText = blankToNull(a);
  const bText = blankToNull(b);
  if (aText === null && bText === null) return 0;
  if (aText === null) return 1;
  if (bText === null) return -1;
  const cmp = aText.localeCompare(bText, 'da', TEXT);
  return direction === 'asc' ? cmp : -cmp;
}

function blankToNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  if (trimmed.length === 0 || trimmed === '—' || trimmed === '–' || trimmed === '-') return null;
  return trimmed;
}

function compareNameTie(a: PatientListSortable, b: PatientListSortable): number {
  const last = a.last_name.localeCompare(b.last_name, 'da', { sensitivity: 'base' });
  if (last !== 0) return last;
  const first = a.first_name.localeCompare(b.first_name, 'da', { sensitivity: 'base' });
  if (first !== 0) return first;
  return a.id.localeCompare(b.id);
}
